import {
  ContentCopy as ContentCopyIcon,
  ReceiptLong as ReceiptLongIcon,
  Search as SearchIcon,
  TrackChanges as TrackChangesIcon,
} from '../components/icons';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Button,
  Alert,
  useMediaQuery,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Snackbar,
  Popover,
  Badge,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCart } from '../contexts/CartContext';
import { PublicThemeProvider, usePublicTheme } from '../contexts/PublicThemeContext';
import menuService from '../services/menuService';
import tableService from '../services/tableService';
import orderService from '../services/orderService';
import MenuCard from '../components/menu/MenuCard';
import MenuListItem from '../components/menu/MenuListItem';
import MenuItemDetailsDialog from '../components/menu/MenuItemDetailsDialog';
import CartDrawer from '../components/cart/CartDrawer';
import CheckoutDialog from '../components/checkout/CheckoutDialog';
import MenuPageSkeleton from '../components/skeletons/MenuPageSkeleton';
import FixedCartWidget from '../components/cart/FixedCartWidget';
import TrackOrderDialog from '../components/order/TrackOrderDialog';
import PaymentStatusDialog from '../components/payment/PaymentStatusDialog';
import { getHoverColor, hexToRgba } from '../utils/colorUtils';
import { getOrderStatusConfig, getOrderStatusLabel } from '../config';
import { formatTimeString } from '../utils/dateUtils';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function getNextOpenInfo(businessHours) {
  if (!businessHours || businessHours.length === 0) return null;

  const now = new Date();
  const currentDay = (now.getDay() + 6) % 7;

  for (let offset = 0; offset < 7; offset++) {
    const dayIndex = (currentDay + offset + 1) % 7;
    const dayHours = businessHours.find((bh) => bh.day_of_week === dayIndex);
    if (dayHours && dayHours.is_open) {
      const dayName = DAY_NAMES[dayIndex];
      if (dayHours.is_24_hours) {
        return `Opens ${offset === 0 ? 'tomorrow' : dayName}`;
      }
      if (dayHours.open_time) {
        return `Opens ${offset === 0 ? 'tomorrow' : dayName} at ${formatTimeString(dayHours.open_time)}`;
      }
      return `Opens ${offset === 0 ? 'tomorrow' : dayName}`;
    }
  }
  return null;
}

function getTodayOpenInfo(businessHours) {
  if (!businessHours || businessHours.length === 0) return null;

  const now = new Date();
  const currentDay = (now.getDay() + 6) % 7;
  const todayHours = businessHours.find((bh) => bh.day_of_week === currentDay);

  if (!todayHours || !todayHours.is_open) return null;
  if (todayHours.is_24_hours) return null;

  const currentTime = now.toTimeString().slice(0, 5);
  if (todayHours.open_time && currentTime < todayHours.open_time) {
    return `Opens today at ${formatTimeString(todayHours.open_time)}`;
  }

  return null;
}

const MenuPageContent = () => {
  const theme = useTheme();
  const { updateMerchantTheme, coverPhoto, coverName, coverDescription, themeReady, primaryColor } = usePublicTheme();

  useEffect(() => {
    if (coverPhoto) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = coverPhoto;
      document.head.appendChild(link);
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [coverPhoto]);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get query parameters
  const queryParams = new URLSearchParams(location.search);
  const merchantId = queryParams.get('merchant');
  const tableId = queryParams.get('table');
  const paymentStatus = queryParams.get('payment'); // 'success' or 'failed'
  const paymentId = queryParams.get('payment_id');
  const orderId = queryParams.get('order_id');
  const trackOrderNumber = queryParams.get('track_order'); // order number for tracking

  useEffect(() => {
    // Theme readiness resets when merchantId changes; fetchData handles the update
  }, [merchantId]);

  const { addItem, setMerchantAndTable } = useCart();

  const [categories, setCategories] = useState([]);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [merchant, setMerchant] = useState(null);
  const [, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(() => {
    try {
      return !!sessionStorage.getItem('merchqry_gcash_payment');
    } catch (_) {}
    return false;
  });
  const [isTrackOrderOpen, setIsTrackOrderOpen] = useState(false);
  const [trackingOrderNumber, setTrackingOrderNumber] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [ordersAnchorEl, setOrdersAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const activeOrders = customerOrders.filter(
    (order) => !['completed', 'cancelled'].includes(order.status)
  );
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Handle payment status from URL parameters
  useEffect(() => {
    if (paymentStatus && paymentId && orderId) {
      setIsPaymentDialogOpen(true);
    }
  }, [paymentStatus, paymentId, orderId]);

  // Handle order tracking from URL parameters
  useEffect(() => {
    if (trackOrderNumber) {
      setIsTrackOrderOpen(true);
    }
  }, [trackOrderNumber]);

  // Fetch merchant, table, and menu data
  useEffect(() => {
    if (merchantId) {
      fetchData();
    } else {
      setError('No merchant specified. Please scan a valid QR code.');
      setLoading(false);
    }
  }, [merchantId, tableId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch customer orders when data is loaded
  useEffect(() => {
    if (!loading && merchantId && tableId) {
      fetchCustomerOrders();
    }
  }, [loading, merchantId, tableId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh customer orders periodically
  useEffect(() => {
    if (!merchantId || !tableId) return;

    const interval = setInterval(() => {
      fetchCustomerOrders();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [merchantId, tableId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const menuData = tableId
        ? await menuService.getPublicTableMenu(merchantId, tableId)
        : await menuService.getPublicMenu(merchantId);

      const merchantData = menuData.merchant;
      setMerchant(merchantData);
      updateMerchantTheme(merchantData);

      let tableData = menuData.table || null;
      if (!tableData && tableId) {
        try {
          tableData = await tableService.getPublicTableInfo(merchantId, tableId);
        } catch (tableError) {
          console.warn('Could not fetch table info, continuing without table context:', tableError);
        }
      }
      setTable(tableData);

      setMerchantAndTable(merchantData, tableData);

      const featuredItemsData = menuData.featured_items || [];
      const categoriesData = menuData.categories || [];

      setFeaturedItems(featuredItemsData);
      setCategories(categoriesData);

      // Reset selected category if it's no longer available
      const categoriesWithItems = categoriesData.filter(category =>
        category.items && category.items.length > 0
      );
      if (selectedCategoryId !== 'all' && !categoriesWithItems.find(cat => cat.id === selectedCategoryId)) {
        setSelectedCategoryId('all');
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load menu. Please try again.');
      setLoading(false);
    }
  };

  // Fetch customer orders
  const fetchCustomerOrders = async () => {
    // Only fetch if we have merchant and table
    if (!merchantId || !tableId) {
      return;
    }

    try {
      const orders = await orderService.getCustomerOrders(merchantId, tableId);
      setCustomerOrders(orders);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      // Don't show error to user, just silently fail
    }
  };

  // Copy order number to clipboard
  const handleCopyOrderNumber = async (orderNumber) => {
    try {
      await navigator.clipboard.writeText(orderNumber.toUpperCase());
      setSnackbarMessage(`Order number ${orderNumber.toUpperCase()} copied to clipboard!`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to copy order number:', error);
      setSnackbarMessage('Failed to copy order number');
      setSnackbarOpen(true);
    }
  };

  const getStatusColor = (status) => getOrderStatusConfig(status).muiColor;
  const getStatusDisplay = (status) => getOrderStatusLabel(status);

  // Handle search (client-side filtering of already-loaded data)
  const handleSearch = (query = searchQuery) => {
    if (!query.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const normalizedQuery = query.trim().toLowerCase();
    const results = categories.flatMap(category =>
      (category.items || []).filter(item =>
        item.name.toLowerCase().includes(normalizedQuery) ||
        (item.description && item.description.toLowerCase().includes(normalizedQuery)) ||
        category.name.toLowerCase().includes(normalizedQuery)
      )
    );
    setSearchResults(results);
  };

  useEffect(() => {
    handleSearch(searchQuery);
  }, [searchQuery, categories]); // eslint-disable-line react-hooks/exhaustive-deps


  // Handle item click
  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  // Handle add to cart
  const handleAddToCart = (item) => {
    addItem(item, item.quantity || 1, item.options || []);
  };

  // Handle checkout
  const handleCheckout = () => {
    setIsCartOpen(false);
    setIsCheckoutOpen(true);
  };

  // Get categories that have items
  const getCategoriesWithItems = () => {
    return categories.filter(category =>
      category.items && category.items.length > 0
    );
  };

  // Filter categories based on selected category and hide empty categories
  const getFilteredCategories = () => {
    const categoriesWithItems = getCategoriesWithItems();

    if (selectedCategoryId === 'all') {
      return categoriesWithItems;
    }
    return categoriesWithItems.filter(category => category.id === selectedCategoryId);
  };

  // Filter featured items based on selected category
  const getFilteredFeaturedItems = () => {
    // Get categories that have items
    const categoriesWithItems = categories.filter(category =>
      category.items && category.items.length > 0
    );

    if (selectedCategoryId === 'all') {
      // Only show featured items from categories that have items
      return featuredItems.filter(item =>
        categoriesWithItems.some(cat => cat.id === item.category.id)
      );
    }
    return featuredItems.filter(item => item.category.id === selectedCategoryId);
  };

  const isPaused = merchant?.settings?.status === 'paused';
  const isOutsideBusinessHours = merchant?.settings?.is_currently_open === false && !isPaused;
  const orderingDisabled = isPaused || isOutsideBusinessHours;
  const orderingDisabledReason = isPaused ? 'paused' : isOutsideBusinessHours ? 'outside_hours' : '';

  // Show skeleton while theme is not ready or data is loading
  if (!themeReady || loading) {
    return <MenuPageSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10, minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #e0f2fe 100%)' }}>
      {/* Enhanced Cover Banner */}
      <Box
        sx={{
          width: '100%',
          minHeight: { xs: 200, sm: 260, md: 480 },
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: coverPhoto ?
            `linear-gradient(135deg, rgba(0,0,0,0.45), rgba(0,0,0,0.55)), url(${coverPhoto})` :
            `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, #764ba2 50%, #f093fb 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: coverPhoto ? 'rgba(0,0,0,0.25)' : 'transparent',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.5,
            zIndex: 1,
          }
        }}
      >
        {/* Cover Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            textAlign: 'center',
            color: 'white',
            px: { xs: 2.5, sm: 3, md: 4 },
            py: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          {/* Restaurant Name */}
          <div>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '3.5rem', lg: '5rem' },
                textShadow: '2px 4px 16px rgba(0,0,0,0.7), 0px 2px 8px rgba(0,0,0,0.5)',
                mb: { xs: 1, sm: 1.5, md: 3 },
                letterSpacing: '-0.02em',
                color: '#ffffff',
                lineHeight: { xs: 1.2, md: 1.1 },
              }}
            >
              {coverName || merchant?.name || 'Restaurant'}
            </Typography>
          </div>

          {/* Restaurant Description */}
          {(coverDescription || merchant?.description) && (
            <div>
              <Typography
                variant="h5"
                component="p"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.25rem', lg: '1.45rem' },
                  textShadow: '1px 2px 8px rgba(0,0,0,0.7), 0px 1px 4px rgba(0,0,0,0.5)',
                  maxWidth: { xs: 360, sm: 500, md: 800 },
                  mx: 'auto',
                  fontWeight: 400,
                  lineHeight: { xs: 1.5, md: 1.7 },
                  color: 'rgba(255, 255, 255, 0.98)',
                }}
              >
                {coverDescription || merchant?.description}
              </Typography>
            </div>
          )}
        </Box>
      </Box>

      {/* Merchant Status Banner */}
      {merchant?.settings?.status === 'busy' && (
        <Box
          sx={{
            width: '100%',
            py: { xs: 1.25, sm: 1.5 },
            px: 2,
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderBottom: '1px solid #f59e0b',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              color: '#92400e',
            }}
          >
            Orders may take longer than usual right now. Thank you for your patience!
          </Typography>
        </Box>
      )}
      {isPaused && (
        <Box
          sx={{
            width: '100%',
            py: { xs: 1.5, sm: 2 },
            px: 2,
            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            borderBottom: '1px solid #ef4444',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              color: '#991b1b',
            }}
          >
            We are currently closed. Please check back later.
          </Typography>
        </Box>
      )}
      {isOutsideBusinessHours && (
        <Box
          sx={{
            width: '100%',
            py: { xs: 1.25, sm: 1.5 },
            px: 2,
            background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
            borderBottom: '1px solid #8b5cf6',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              color: '#5b21b6',
            }}
          >
            {"We're currently outside business hours. "}
            {getTodayOpenInfo(merchant?.settings?.business_hours) ||
              getNextOpenInfo(merchant?.settings?.business_hours) ||
              'Please check back during our operating hours.'}
          </Typography>
        </Box>
      )}

      {/* Fixed Header with Search and Track Order */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(35px)',
          borderRadius: 0,
          py: { xs: 2, sm: 3.5 },
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 3 },
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search delicious items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size={isMobile ? "small" : "medium"}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: '260px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: { xs: 3, sm: 5 },
                  background: '#ffffff',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 500,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                  '&.Mui-focused': {
                    boxShadow: `0 10px 35px ${hexToRgba(primaryColor, 0.3)}`,
                    transform: 'translateY(-2px)',
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleSearch()}
                      edge="end"
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        width: { xs: 36, sm: 44 },
                        height: { xs: 36, sm: 44 },
                        borderRadius: { xs: '8px', sm: '12px' },
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
                        color: '#fff',
                        boxShadow: `0 4px 14px ${hexToRgba(primaryColor, 0.35)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        mr: { xs: -0.5, sm: -0.5 },
                        '&:hover': {
                          background: `linear-gradient(135deg, ${getHoverColor(primaryColor, 'darken', 10)} 0%, ${getHoverColor(primaryColor, 'darken', 20)} 100%)`,
                          transform: 'scale(1.08)',
                          boxShadow: `0 6px 20px ${hexToRgba(primaryColor, 0.45)}`,
                        }
                      }}
                    >
                      <SearchIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{
              display: 'flex',
              gap: { xs: 1.5, sm: 2 },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <FormControl size={isMobile ? "small" : "medium"} sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 190 } }}>
                <Select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: { xs: 3, sm: 5 },
                    background: '#ffffff',
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& fieldset': {
                      border: 'none',
                    }
                  }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {getFilteredCategories().map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Track Order" arrow>
                <IconButton
                  onClick={() => setIsTrackOrderOpen(true)}
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: { xs: '10px', sm: '14px' },
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
                    color: '#fff',
                    boxShadow: `0 6px 20px ${hexToRgba(primaryColor, 0.4)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${getHoverColor(primaryColor, 'darken', 10)} 0%, ${getHoverColor(primaryColor, 'darken', 20)} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 10px 30px ${hexToRgba(primaryColor, 0.5)}`,
                    }
                  }}
                >
                  <TrackChangesIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
              </Tooltip>
              {activeOrders.length > 0 && (
                <Tooltip title="Your Orders" arrow>
                  <IconButton
                    onClick={(e) => setOrdersAnchorEl(e.currentTarget)}
                    sx={{
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      borderRadius: { xs: '10px', sm: '14px' },
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
                      color: '#fff',
                      boxShadow: `0 6px 20px ${hexToRgba(primaryColor, 0.4)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${getHoverColor(primaryColor, 'darken', 10)} 0%, ${getHoverColor(primaryColor, 'darken', 20)} 100%)`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 10px 30px ${hexToRgba(primaryColor, 0.5)}`,
                      }
                    }}
                  >
                    <Badge
                      badgeContent={activeOrders.length}
                      sx={{
                        '& .MuiBadge-badge': {
                          background: '#e53935',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          minWidth: 18,
                          height: 18,
                          top: -4,
                          right: -4,
                          boxShadow: '0 2px 6px rgba(229, 57, 53, 0.4)',
                        }
                      }}
                    >
                      <ReceiptLongIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Container>
      </Paper>

      {/* Your Orders Dropdown Popover */}
      <Popover
        open={Boolean(ordersAnchorEl)}
        anchorEl={ordersAnchorEl}
        onClose={() => setOrdersAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              borderRadius: { xs: 2, sm: 3 },
              width: { xs: 'calc(100vw - 32px)', sm: 380 },
              maxHeight: { xs: '60vh', sm: 440 },
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${hexToRgba(primaryColor, 0.12)}`,
            }
          }
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              color: '#fff',
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
            }}
          >
            Your Orders
          </Typography>
          <Chip
            label={activeOrders.length}
            size="small"
            sx={{
              background: 'rgba(255, 255, 255, 0.25)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.75rem',
              height: 22,
            }}
          />
        </Box>
        <Box sx={{ overflowY: 'auto', maxHeight: { xs: 'calc(60vh - 56px)', sm: 380 } }}>
          {activeOrders.map((order, index) => (
            <Box
              key={order.id}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderBottom: index < activeOrders.length - 1 ? `1px solid ${hexToRgba(primaryColor, 0.08)}` : 'none',
                transition: 'background 0.2s ease',
                '&:hover': {
                  background: hexToRgba(primaryColor, 0.04),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Box
                  onClick={() => handleCopyOrderNumber(order.order_number)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.75 },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontWeight: 700,
                      color: primaryColor,
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    }}
                  >
                    {order.order_number.toUpperCase()}
                  </Typography>
                  <ContentCopyIcon sx={{ fontSize: 13, color: hexToRgba(primaryColor, 0.6) }} />
                </Box>
                <Chip
                  label={getStatusDisplay(order.status)}
                  color={getStatusColor(order.status)}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: '#6b7280',
                  fontSize: { xs: '0.78rem', sm: '0.82rem' },
                  lineHeight: 1.5,
                }}
              >
                {order.items.map((item, idx) => (
                  <span key={idx}>
                    {item.name} x{item.quantity}
                    {idx < order.items.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        {/* Search results heading */}
        {isSearching && (
          <Box mb={5}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.75rem' },
                mb: 3,
                letterSpacing: '-0.02em',
                color: '#1f2937',
              }}
            >
              Search Results for "{searchQuery}"
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size={isMobile ? 'small' : 'medium'}
              onClick={() => {
                setSearchQuery('');
                setIsSearching(false);
              }}
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 2, sm: 3 },
                py: { xs: 0.5, sm: 0.75 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                borderWidth: 1.5,
                borderColor: '#ef4444',
                color: '#ef4444',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: '#ef4444',
                  color: '#fff',
                  borderWidth: 1.5,
                  borderColor: '#ef4444',
                }
              }}
            >
              Clear Search
            </Button>
            <Divider sx={{ mt: 4, mb: 2 }} />
          </Box>
        )}

        {/* Search Results */}
        {isSearching && (
          <Box mb={4}>
            {searchResults.length === 0 ? (
              <Box textAlign="center" py={5}>
                <Typography variant="h6" color="text.secondary">
                  No items found matching your search
                </Typography>
              </Box>
            ) : (
              <Box>
                {searchResults.map((item) => (
                  <MenuListItem
                    key={item.id}
                    item={item}
                    onAddToCart={handleAddToCart}
                    onViewDetails={handleItemClick}
                    showCategory={true}
                    orderingDisabled={orderingDisabled}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Main Menu Content - Only show when not searching */}
        {!isSearching && (
          <>
            {/* Best Sellers Section */}
            {getFilteredFeaturedItems().length > 0 && (
              <Box mb={8}>
                <div>
                  <Box sx={{ mb: 6, textAlign: 'center' }}>

                    <Typography
                      variant="h2"
                      component="h2"
                      sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3.5rem' },
                        mb: 2,
                        letterSpacing: '-0.02em',
                        color: '#1f2937',
                      }}
                    >
                     Our Best Sellers
                    </Typography>
                    {selectedCategoryId !== 'all' && (
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontWeight: 500,
                          color: '#6b7280',
                          fontSize: '1.25rem',
                        }}
                      >
                        from {getFilteredCategories().find(cat => cat.id === selectedCategoryId)?.name}
                      </Typography>
                    )}
                  </Box>
                </div>
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 4 }} sx={{ mb: { xs: 4, sm: 6 } }}>
                  {getFilteredFeaturedItems().map((item) => (
                    <Grid item xs={6} sm={6} md={4} key={item.id}>
                      <MenuCard
                        item={item}
                        onAddToCart={handleAddToCart}
                        onViewDetails={handleItemClick}
                        showCategory={selectedCategoryId === 'all'}
                        orderingDisabled={orderingDisabled}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Divider
                  sx={{
                    mb: 6,
                    '&::before, &::after': {
                      borderColor: `${primaryColor}40`,
                    }
                  }}
                />
              </Box>
            )}

            {/* Categories with Menu Items */}
            {getFilteredCategories().map((category, index) => (
              <Box key={category.id} mb={8}>
                <div>
                  <Box sx={{ mb: 5, textAlign: 'left' }}>
                    <Typography
                      variant="h2"
                      component="h2"
                      sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.30rem' },
                        mb: 2,
                        letterSpacing: '-0.02em',
                        color: '#1f2937',
                      }}
                    >
                      {category.name}
                    </Typography>
                  </Box>
                </div>

                <Box>
                  {category.items.map((item) => (
                    <MenuListItem
                      key={item.id}
                      item={item}
                      onAddToCart={handleAddToCart}
                      onViewDetails={handleItemClick}
                      showCategory={false}
                      orderingDisabled={orderingDisabled}
                    />
                  ))}
                </Box>

                {/* Add divider between categories except for the last one */}
                {category.id !== getFilteredCategories()[getFilteredCategories().length - 1]?.id && (
                  <Divider sx={{ mt: 4 }} />
                )}
              </Box>
            ))}

            {/* Show message if no categories with items */}
            {getFilteredCategories().length === 0 && (
              <Box textAlign="center" py={5}>
                <Typography variant="h6" color="text.secondary">
                  {selectedCategoryId === 'all'
                    ? 'No menu items available'
                    : 'No items available in the selected category'
                  }
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>

      {/* Fixed cart widget */}
      {!orderingDisabled && (
        <FixedCartWidget onClick={() => setIsCartOpen(true)} />
      )}

      {/* Item details dialog */}
      <MenuItemDetailsDialog
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
        onAddToCart={handleAddToCart}
        orderingDisabled={orderingDisabled}
        orderingDisabledReason={orderingDisabledReason}
      />

      {/* Cart drawer */}
      <CartDrawer
        open={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        onCheckout={handleCheckout}
      />

      {/* Checkout dialog */}
      <CheckoutDialog
        open={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onTrackOrder={(orderNumber) => {
          setTrackingOrderNumber(orderNumber);
          setIsTrackOrderOpen(true);
        }}
      />

      {/* Track Order dialog */}
      <TrackOrderDialog
        open={isTrackOrderOpen}
        onClose={() => {
          setIsTrackOrderOpen(false);
          setTrackingOrderNumber('');
        }}
        initialOrderNumber={trackingOrderNumber || trackOrderNumber || ''}
      />

      {/* Payment Status Dialog */}
      <PaymentStatusDialog
        open={isPaymentDialogOpen}
        onClose={() => {
          setIsPaymentDialogOpen(false);
          // Clean up URL parameters after closing dialog
          const newParams = new URLSearchParams(location.search);
          newParams.delete('payment');
          newParams.delete('payment_id');
          newParams.delete('order_id');
          navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
        }}
        paymentStatus={paymentStatus}
        paymentId={paymentId}
        orderId={orderId}
        onRetryPayment={async (order) => {
          // GCash retries are handled in the checkout dialog; reopen the menu
          // so the customer can re-initiate their order payment.
          try {
            const { retryGcashPayment } = await import('../services/paymentService');
            await retryGcashPayment(order.id);
          } catch (error) {
            console.error('Error retrying payment:', error);
          }
        }}
      />

      {/* Snackbar for copy notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Wrap the MenuPageContent with PublicThemeProvider
const MenuPage = () => {
  return (
    <PublicThemeProvider>
      <MenuPageContent />
    </PublicThemeProvider>
  );
};

export default MenuPage;
